import { cn } from "@/lib/utils";

export type DashboardRange = "today" | "week" | "month" | "year";

const OPTIONS: { value: DashboardRange; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
];

/** Start date for a dashboard range (local time). */
export const rangeStart = (range: DashboardRange): Date => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (range) {
    case "today":
      return start;
    case "week": {
      const day = (start.getDay() + 6) % 7; // Monday = 0
      start.setDate(start.getDate() - day);
      return start;
    }
    case "month":
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case "year":
      return new Date(now.getFullYear(), 0, 1);
  }
};

/**
 * Compact segmented date filter (reference style: quiet options, active
 * segment in dark navy with white text).
 */
export const DateRangeSelector = ({
  value,
  onChange,
}: {
  value: DashboardRange;
  onChange: (range: DashboardRange) => void;
}) => (
  <div
    role="tablist"
    aria-label="Date range"
    className="flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5"
  >
    {OPTIONS.map((option) => (
      <button
        key={option.value}
        role="tab"
        aria-selected={value === option.value}
        onClick={() => onChange(option.value)}
        className={cn(
          "h-7 rounded-md px-2.5 text-[12.5px] font-medium transition-colors",
          value === option.value
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);
